import { Modal } from "@/Common/Components/Modals/Modal";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { BritishColumbiaObstetricalFilePage, BritishColumbiaObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/BritishColumbia/BritishColumbiaObstetricalFilePage";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";
import { BritishColumbiaObstetricalVisit } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/BritishColumbiaObstetricalVisit";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { PrenatalVisitInitials } from "@/Patient/Models/ObstetricalFile/PrenatalVisitInitials";
import { BritishColumbiaObstetricalFileSection12 } from "./Subcomponent/BritishColumbiaObstetricalFileSection12";
import { BritishColumbiaObstetricalFileSection13 } from "./Subcomponent/BritishColumbiaObstetricalFileSection13";
import { BritishColumbiaObstetricalFileSection14 } from "./Subcomponent/BritishColumbiaObstetricalFileSection14";
import { BritishColumbiaObstetricalFileSection15 } from "./Subcomponent/BritishColumbiaObstetricalFileSection15";
import { BritishColumbiaObstetricalFileSection16 } from "./Subcomponent/BritishColumbiaObstetricalFileSection16";
import { BritishColumbiaObstetricalFileSection18 } from "./Subcomponent/BritishColumbiaObstetricalFileSection18";
import { BritishColumbiaObstetricalPatientInformationSection } from "./Subcomponent/BritishColumbiaObstetricalPatientInformationSection";
import { BritishColumbiaObstetricalFileSubsequentVisitsTable } from "./Subcomponent/Section17/BritishColumbiaObstetricalFileSubsequentVisitsTable";

class BritishColumbiaObstetricalFilePart2PageState extends BritishColumbiaObstetricalFilePageState {
    public isReuseVitalSignPopupShown: boolean;
}

class BritishColumbiaObstetricalFilePart2 extends BritishColumbiaObstetricalFilePage<BritishColumbiaObstetricalFilePart2PageState> {
    private readonly componentId = "british-columbia-obstetrical-file-part2";
    private pendingFormUpdate = false;
    private newEmrTemplateId: string;

    public componentDidMount(): void {
        super.componentDidMount();
        this.checkReuseVitalSign();

        if (this.pendingFormUpdate) {
            this.handleFormUpdate();
            this.pendingFormUpdate = false;
        }

        if (this.newEmrTemplateId)
            this.handleObstetricalFileVitalSignLink(this.newEmrTemplateId);
    }

    public componentDidUpdate(prevProps: IObstetricalFilePageProps<BritishColumbiaObstetricalFileForm>): void {
        super.componentDidUpdate(prevProps);

        if (this.props.readonly !== prevProps.readonly)
            this.checkReuseVitalSign();
    }

    protected buildState(props: IObstetricalFilePageProps<BritishColumbiaObstetricalFileForm>): BritishColumbiaObstetricalFilePart2PageState {
        const state = new BritishColumbiaObstetricalFilePart2PageState();
        state.form = new BritishColumbiaObstetricalFileForm(props.form);

        const currentVisit = state.form.Section17.firstOrDefault(v => v.ContributionId === this.props.contributionId);
        const emrTemplateIdBefore = currentVisit?.EmrTemplateId;

        state.form.setSubsequentVisitsVitalSigns(
            props.obstetricalFileMedicalSummary,
            props.encounter.ConsultationDate,
            props.contributionCreationDate,
            props.contributionId
        );

        this.newEmrTemplateId = currentVisit?.EmrTemplateId !== emrTemplateIdBefore ? currentVisit?.EmrTemplateId : null;

        if (!state.form.Section17.any()) {
            this.initializePrenatalVisit(state.form, props);
            this.pendingFormUpdate = true;
        }

        return state;
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId} className="bc-antenatal-part2">
                <div className="border border-bottom flex-display">
                    {this.renderSections12To14()}
                    {this.renderRightSections()}
                </div>
                {this.renderSubsequentVisitsSection17()}
                <BritishColumbiaObstetricalFileSection18 {...this.getSubcomponentBaseProps()} />
            </div>
        );
    }

    protected async onVitalSignUpdated(): Promise<void> {
        if (IsNullOrUndefined(this.state.currentVisitId))
            return;

        const visit = this.state.form.Section17.firstOrDefault(p => p.getId() === this.state.currentVisitId);

        if (!visit)
            return;

        const updatedVitalSign = await super.handleVitalSignUpdate(
            visit.vitalSign,
            visit.EmrTemplateId
        );

        this.mutateState(
            s => s.form.Section17.mutate(
                item => {
                    item.updateVitalSignValueFromMedicalItem(updatedVitalSign);
                    item.EmrTemplateId = updatedVitalSign?.EMRTemplateId;
                },
                item => item.getId() === this.state.currentVisitId
            ),
            () => this.handleObstetricalFileVitalSignLink(updatedVitalSign?.EMRTemplateId)
        );
    }

    private renderSections12To14(): JSX.Element {
        return (
            <div className="border border-right" style={{ width: "60%" }}>
                <BritishColumbiaObstetricalFileSection12 {...this.getSubcomponentBaseProps()} />
                <BritishColumbiaObstetricalFileSection13 {...this.getSubcomponentBaseProps()} />
                <BritishColumbiaObstetricalFileSection14 {...this.getSubcomponentBaseProps()} />
            </div>
        );
    }

    private renderRightSections(): JSX.Element {
        return (
            <div className="min-width-right-section" style={{ width: "40%" }}>
                <BritishColumbiaObstetricalPatientInformationSection {...this.getSubcomponentBaseProps()} />
                <BritishColumbiaObstetricalFileSection15 {...this.getSubcomponentBaseProps()} />
                <BritishColumbiaObstetricalFileSection16 {...this.getSubcomponentBaseProps()} />
            </div>
        );
    }

    private renderSubsequentVisitsSection17(): JSX.Element {
        return (
            <div className="section border border-bottom subsequent-visits">
                <BritishColumbiaObstetricalFileSubsequentVisitsTable
                    {...this.getSubcomponentBaseProps()}
                    visits={this.state.form.Section17}
                    onChange={(visits: BritishColumbiaObstetricalVisit[], currentVisitChange: BritishColumbiaObstetricalVisit, newInitialsMapping: string, shouldHandleFormUpdate: boolean) =>
                        this.onSubsequentVisitsTableChange(visits, currentVisitChange, newInitialsMapping, shouldHandleFormUpdate)
                    }
                    onAddSubsequentVisitRow={!this.props.readonly && !this.contributionHasSubsequentVisitRow()
                        ? () => this.addSubsequentVisitRow(this.props.contributionId, this.props.encounter.ConsultationDate)
                        : null
                    }
                    onWeightUnitChange={unit => this.mutateModelStateWithFormUpdate(s => s.form.Metadata.SubsequentVisitsTableWeightUnit = unit)}
                    onVitalSignUpdated={() => this.onVitalSignUpdated()}
                    vitalSignControlRenderer={this.vitalSignControlRenderer}
                    existingInitials={this.state.form.Section17.map(visit => visit.Initials)}
                    heightInMeters={null} // TODO: get height from vital signs
                    obstetricalFileMedicalSummary={this.props.obstetricalFileMedicalSummary}
                />
                {this.state.isReuseVitalSignPopupShown && this.renderVitalSignOptionPopup()}
            </div>
        );
    }

    private renderVitalSignOptionPopup(): JSX.Element {
        const footer = (
            <div className="modal-footer">
                <button
                    className="btn btn-default"
                    onClick={() => this.closeReuseVitalSignPopup(null, () => this.handleFormUpdate())}
                >
                    {cacheStore.GetString("NewVitalSignsLabel")}
                </button>
                <button
                    className="btn btn-primary"
                    onClick={() => this.onReuseVitalSign()}
                >
                    {cacheStore.GetString("ReuseLabel")}
                </button>
            </div>
        );

        return (
            <Modal
                title={cacheStore.GetString("ObstetricalFile_ReusingVitalSignsTitleLabel")}
                showOverlay
                disableOverlayClose
                roundTopCorner
                footer={footer}
                onClose={() => this.mutateState(s => s.isReuseVitalSignPopupShown = false)}
            >
                <div>
                    {cacheStore.GetString("ObstetricalFile_ReusingVitalSignsQuestionBCLabel")}
                </div>
            </Modal>
        );
    }

    protected addSubsequentVisitRow(contributionId: string, consultationDate: Date): void {
        this.mutateModelState(
            s => {
                const gestationalAge = ObstetricsCalculator.calculateWeeksOfGestation(
                    s.form.Section12.ConfirmedEstimatedDateOfDelivery ?? s.form.Section3.EstimatedDateOfBirthByUltrasound ?? s.form.Section3.EstimatedDateOfBirthByLastMenstrualPeriod,
                    consultationDate
                );

                const vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                    obstetricalFileMedicalSummary: this.props.obstetricalFileMedicalSummary,
                    consultationDate: this.props.encounter.ConsultationDate,
                    obstetricalFileContributionDate: this.props.contributionCreationDate,
                    skipLoadLastHour: false,
                    weightUnit: s.form.Metadata.SubsequentVisitsTableWeightUnit
                });

                s.form.Section17 = [
                    ...(s.form.Section17 ?? []),
                    new BritishColumbiaObstetricalVisit({
                        ContributionId: contributionId,
                        Date: consultationDate,
                        GestationalAge: gestationalAge,
                        vitalSign,
                        EmrTemplateId: vitalSign?.emrTemplateId
                    })
                ];
            },
            () => {
                const subsequentVisit = this.state.form.Section17.firstOrDefault(p => p.ContributionId === this.props.contributionId);

                if (subsequentVisit?.EmrTemplateId)
                    this.handleObstetricalFileVitalSignLink(subsequentVisit.EmrTemplateId);
                else if (subsequentVisit)
                    this.handleFormUpdate();
            }
        );
    }

    private onSubsequentVisitsTableChange(visits: BritishColumbiaObstetricalVisit[], currentVisitChange: BritishColumbiaObstetricalVisit, newInitialsMapping: string, shouldHandleFormUpdate: boolean): void {
        this.mutateModelState(
            s => {
                s.form.Section17 = visits;
                s.currentVisitId = currentVisitChange.getId();
            },
            () => {
                if (shouldHandleFormUpdate)
                    this.handleFormUpdate();
            }
        );
    }

    private contributionHasSubsequentVisitRow(): boolean {
        return this.state.form.Section17.any(visit => visit.ContributionId === this.props.contributionId);
    }

    private checkReuseVitalSign(): void {
        if (!this.props.readonly
            && !this.state.form.Metadata.IsReuseVitalSignOptionSet
            && !!this.state.form.Section10.VitalSignEmrTemplateId
            && this.state.form.Section17.any()
            && !this.state.form.Section17[0].EmrTemplateId
            && this.isValidVitalSignForVisit()) {
            this.mutateState(s => {
                s.form.Metadata.IsReuseVitalSignOptionSet = true;
                s.isReuseVitalSignPopupShown = true;
            });
        }
    }

    private isValidVitalSignForVisit(): boolean {
        const { obstetricalFileMedicalSummary, encounter } = this.props;

        const vitalSign = obstetricalFileMedicalSummary.getVitalSignBy(this.state.form.Section10.VitalSignEmrTemplateId, encounter.ConsultationDate);

        return vitalSign
            && (vitalSign.Weight || vitalSign.BPDia || vitalSign.BPSys)
            && LuxonDateTimeParser.parse(vitalSign.PharmaStartDate).hasSame(LuxonDateTimeParser.parse(encounter.ConsultationDate), "day");
    }

    private closeReuseVitalSignPopup(mutation?: (state: BritishColumbiaObstetricalFilePart2PageState) => void, then?: () => void): void {
        this.mutateState(
            s => {
                s.isReuseVitalSignPopupShown = false;
                mutation?.(s);
            },
            then
        );
    }

    private onReuseVitalSign(): void {
        this.closeReuseVitalSignPopup(
            s => {
                const firstVisit = s.form.Section17[0];
                const { obstetricalFileMedicalSummary, encounter, contributionCreationDate } = this.props;
                const emrTemplateId = s.form.Section10.VitalSignEmrTemplateId;
                s.currentVisitId = firstVisit.getId();

                const vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                    obstetricalFileMedicalSummary: obstetricalFileMedicalSummary,
                    emrTemplateId: emrTemplateId,
                    consultationDate: encounter.ConsultationDate,
                    obstetricalFileContributionDate: contributionCreationDate
                });
                firstVisit.vitalSign = vitalSign;

                if (!firstVisit.Initials)
                    firstVisit.Initials = new PrenatalVisitInitials();

                firstVisit.Initials.addInitialsFromUser(sessionStore.getCurrentEmployee(), this.existingInitials, ", ");
            },
            () => this.onVitalSignUpdated()
        );
    }

    private get existingInitials(): PrenatalVisitInitials[] {
        return this.state.form.Section17.map(v => v.Initials);
    }
}

export { BritishColumbiaObstetricalFilePart2 };
